import { useState, useEffect, useRef } from 'react'
import { Camera, Edit3, Key, AlertCircle, Upload } from 'lucide-react'
import { Button } from '@quest-lock/ui/button'
import { Input } from '@quest-lock/ui/input'
import { Label } from '@quest-lock/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@quest-lock/ui/dialog'
import { Html5Qrcode } from 'html5-qrcode'
import { TOTPGenerator } from '@/authenticator/TOTPGenerator'
import { QRCodeData } from '@/authenticator/Types'
import { toast } from 'sonner'

interface AddAuthenticatorModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd: (
    name: string,
    secret: string,
    issuer?: string,
    account?: string
  ) => Promise<void>
  existingSecrets?: string[]
}

type TabType = 'manual' | 'qr'

const AddAuthenticatorModal = ({
  isOpen,
  onClose,
  onAdd,
  existingSecrets = []
}: AddAuthenticatorModalProps) => {
  const [activeTab, setActiveTab] = useState<TabType>('manual')
  const [isLoading, setIsLoading] = useState(false)

  const [manualForm, setManualForm] = useState({
    name: '',
    secret: '',
    issuer: '',
    account: ''
  })

  const [qrData, setQrData] = useState<QRCodeData | null>(null)
  const [qrName, setQrName] = useState('')
  const [scannerError, setScannerError] = useState<string | null>(null)
  const [isCameraActive, setIsCameraActive] = useState(false)

  const totpGenerator = new TOTPGenerator()
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null)
  const qrReaderIdRef = useRef(
    'qr-reader-' + Math.random().toString(36).slice(2, 11)
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = () => {
    setManualForm({ name: '', secret: '', issuer: '', account: '' })
    setQrData(null)
    setQrName('')
    setScannerError(null)
    setIsCameraActive(false)
    setActiveTab('manual')
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const validateSecret = (secret: string): boolean => {
    const cleaned = secret.replace(/\s/g, '').toUpperCase()
    return totpGenerator.validateSecret(cleaned)
  }

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!manualForm.name.trim()) {
      toast.error('Please enter a name for this authenticator')
      return
    }

    if (!manualForm.secret.trim()) {
      toast.error('Please enter the secret key')
      return
    }

    const cleanedSecret = totpGenerator.formatSecret(manualForm.secret)

    if (!validateSecret(cleanedSecret)) {
      toast.error('Invalid secret key. Please check and try again.')
      return
    }

    if (existingSecrets.includes(cleanedSecret)) {
      toast.error('This authenticator is already registered')
      return
    }

    setIsLoading(true)
    try {
      await onAdd(
        manualForm.name.trim(),
        cleanedSecret,
        manualForm.issuer.trim() || undefined,
        manualForm.account.trim() || undefined
      )
      resetState()
    } catch (error) {
      console.error('Failed to add authenticator:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleQrSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!qrData) {
      toast.error('No QR code data found')
      return
    }

    if (!qrName.trim()) {
      toast.error('Please enter a name for this authenticator')
      return
    }

    if (existingSecrets.includes(qrData.secret)) {
      toast.error('This authenticator is already registered')
      return
    }

    setIsLoading(true)
    try {
      await onAdd(qrName.trim(), qrData.secret, qrData.issuer, qrData.account)
      resetState()
    } catch (error) {
      console.error('Failed to add authenticator:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleQrScan = (decodedText: string) => {
    try {
      const parsedData = totpGenerator.parseQRCodeURL(decodedText)
      setQrData(parsedData)
      setScannerError(null)

      // Auto-generate name from QR data
      if (parsedData.issuer && parsedData.account) {
        setQrName(`${parsedData.issuer} (${parsedData.account})`)
      } else if (parsedData.issuer) {
        setQrName(parsedData.issuer)
      } else if (parsedData.account) {
        setQrName(parsedData.account)
      } else {
        setQrName('New Authenticator')
      }

      stopScanning()
      toast.success('QR code scanned successfully!')
    } catch (error) {
      console.error('QR scan error:', error)
      setScannerError(
        error instanceof Error ? error.message : 'Failed to parse QR code'
      )
    }
  }

  const startScanning = async () => {
    setScannerError(null)
    setIsCameraActive(true)

    setTimeout(async () => {
      try {
        if (!html5QrCodeRef.current) {
          html5QrCodeRef.current = new Html5Qrcode(qrReaderIdRef.current)
        }

        await html5QrCodeRef.current.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 }
          },
          handleQrScan,
          () => {
            // Error callback for each frame - ignore
          }
        )
      } catch (error) {
        console.error('Failed to start scanner:', error)
        setIsCameraActive(false)
        setScannerError('Failed to start camera. Please check permissions.')
      }
    }, 100)
  }

  const stopScanning = async () => {
    try {
      if (html5QrCodeRef.current?.isScanning) {
        await html5QrCodeRef.current.stop()
      }
    } catch (error) {
      console.error('Failed to stop scanner:', error)
    } finally {
      setIsCameraActive(false)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setScannerError(null)

    try {
      const tempScanner = new Html5Qrcode('temp-file-scanner')
      const result = await tempScanner.scanFileV2(file, true)
      if (result.decodedText) {
        handleQrScan(result.decodedText)
      }
    } catch (error) {
      console.error('Failed to scan image:', error)
      setScannerError(
        'Failed to read QR code from image. Please try another image.'
      )
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current?.isScanning) {
        html5QrCodeRef.current.stop().catch(console.error)
      }
    }
  }, [])

  useEffect(() => {
    if (!isOpen && html5QrCodeRef.current?.isScanning) {
      stopScanning()
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <div id="temp-file-scanner" className="hidden" />
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Key className="size-5" />
            <span>Add Authenticator</span>
          </DialogTitle>
        </DialogHeader>

        <div className="bg-shire-bark/10 flex rounded-lg p-1">
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'manual'
                ? 'text-shire-dark bg-white shadow-sm'
                : 'text-shire-bark hover:text-shire-dark'
            }`}
          >
            <Edit3 className="mr-2 inline size-4" />
            Manual Entry
          </button>
          <button
            onClick={() => setActiveTab('qr')}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === 'qr'
                ? 'text-shire-dark bg-white shadow-sm'
                : 'text-shire-bark hover:text-shire-dark'
            }`}
          >
            <Camera className="mr-2 inline size-4" />
            QR Scanner
          </button>
        </div>

        {activeTab === 'manual' && (
          <form onSubmit={handleManualSubmit} className="space-y-4">
            <div>
              <Label htmlFor="manual-name">Name *</Label>
              <Input
                id="manual-name"
                type="text"
                placeholder="e.g., Google, GitHub, etc."
                value={manualForm.name}
                onChange={e =>
                  setManualForm(prev => ({ ...prev, name: e.target.value }))
                }
                required
              />
            </div>

            <div>
              <Label htmlFor="manual-secret">Secret Key *</Label>
              <Input
                id="manual-secret"
                type="text"
                placeholder="Base32 secret from your service"
                value={manualForm.secret}
                onChange={e =>
                  setManualForm(prev => ({ ...prev, secret: e.target.value }))
                }
                required
              />
              <p className="text-shire-bark/70 mt-1 text-xs">
                Enter the secret key provided by your service (spaces will be
                removed)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="manual-issuer">Issuer</Label>
                <Input
                  id="manual-issuer"
                  type="text"
                  placeholder="Service name"
                  value={manualForm.issuer}
                  onChange={e =>
                    setManualForm(prev => ({ ...prev, issuer: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label htmlFor="manual-account">Account</Label>
                <Input
                  id="manual-account"
                  type="text"
                  placeholder="Username/email"
                  value={manualForm.account}
                  onChange={e =>
                    setManualForm(prev => ({
                      ...prev,
                      account: e.target.value
                    }))
                  }
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Adding...' : 'Add Authenticator'}
              </Button>
            </div>
          </form>
        )}

        {activeTab === 'qr' && (
          <div className="space-y-4">
            {!qrData ? (
              <div className="space-y-4">
                {!isCameraActive ? (
                  <div className="pt-8 text-center">
                    <Camera className="text-shire-bark/50 mx-auto mb-3 size-12" />
                    <p className="text-shire-bark mb-4">
                      Scan the QR code from your authenticator app or service
                    </p>
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                      <Button onClick={startScanning}>
                        <Camera className="mr-2 size-4" />
                        Start Camera
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 size-4" />
                        Upload Image
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div
                      id={qrReaderIdRef.current}
                      className="overflow-hidden rounded-lg"
                    />
                    <div className="flex justify-center gap-2">
                      <Button variant="outline" onClick={stopScanning}>
                        Stop Scanning
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 size-4" />
                        Upload Image
                      </Button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                )}

                {scannerError && (
                  <div className="flex items-center space-x-2 rounded-lg bg-red-50 p-3 text-red-700">
                    <AlertCircle className="size-4 shrink-0" />
                    <span className="text-sm">{scannerError}</span>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleQrSubmit} className="space-y-4">
                <div className="rounded-lg bg-green-50 p-4">
                  <h4 className="mb-2 font-medium text-green-800">
                    QR Code Detected
                  </h4>
                  <div className="space-y-1 text-sm text-green-700">
                    {qrData.issuer && (
                      <p>
                        <strong>Issuer:</strong> {qrData.issuer}
                      </p>
                    )}
                    {qrData.account && (
                      <p>
                        <strong>Account:</strong> {qrData.account}
                      </p>
                    )}
                    <p>
                      <strong>Secret:</strong> {qrData.secret.slice(0, 8)}...
                    </p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="qr-name">Name *</Label>
                  <Input
                    id="qr-name"
                    type="text"
                    value={qrName}
                    onChange={e => setQrName(e.target.value)}
                    required
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setQrData(null)}
                  >
                    Scan Again
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Adding...' : 'Add Authenticator'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AddAuthenticatorModal
